import { ChartWaitlistHistory } from "@/components/chart-waitlist-history";
import { CopyId } from "@/components/copy-id";
import { SiteHeader } from "@/components/site-header";
import { TimeRangeSelector } from "@/components/time-range-selector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import { ApiError } from "@/providers/base.provider";
import {
  type Paginated,
  type WaitlistEntry,
  type WaitlistSortBy,
  waitlistProvider,
} from "@/providers/waitlist.provider";
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconGift,
  IconPencil,
} from "@tabler/icons-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";
import { toast } from "sonner";

const COLUMNS = 8;

// Same sort applies to both the table and the CSV export.
function SortableHead({
  column,
  sortBy,
  sortDesc,
  onSort,
  children,
}: {
  column: WaitlistSortBy;
  sortBy: WaitlistSortBy | undefined;
  sortDesc: boolean;
  onSort: (column: WaitlistSortBy) => void;
  children: ReactNode;
}) {
  const active = sortBy === column;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group flex items-center gap-1 text-xs font-medium text-inherit"
        aria-label={`Sort by ${children}${active ? (sortDesc ? ", descending" : ", ascending") : ""}`}
      >
        {children}
        {active ? (
          sortDesc ? (
            <IconArrowDown className="size-3.5" />
          ) : (
            <IconArrowUp className="size-3.5" />
          )
        ) : (
          <IconArrowsSort className="size-3.5 opacity-0 transition-opacity group-hover:opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

function updateErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "That signup no longer exists.";
  }
  return "Couldn't save that change. Try again.";
}

function EditableEmailCell({
  value,
  disabled,
  onSave,
  type = "email",
  label = "Edit email",
  // app_store_email/play_store_email clear to null. notes is the odd one out
  // per the API: null on PATCH means "leave untouched", only "" clears it.
  clearValue = null,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (value: string | null) => Promise<unknown>;
  type?: "email" | "text";
  label?: string;
  clearValue?: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  if (!editing) {
    return (
      <div className="group flex min-w-0 items-center gap-1">
        <span className="min-w-0 truncate text-xs">
          {value ?? (
            <span className="italic text-muted-foreground opacity-40">—</span>
          )}
        </span>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={() => {
            setDraft(value ?? "");
            setEditing(true);
          }}
          className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-0"
        >
          <IconPencil className="size-3.5" />
        </button>
      </div>
    );
  }

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed === "" ? clearValue : trimmed;
    if (next === value || (next === clearValue && !value)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // The mutation's onError already toasted; stay in edit mode so the
      // admin can retry or back out.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Input
      autoFocus
      type={type}
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(value ?? "");
          setEditing(false);
        }
      }}
      className="h-7 w-full min-w-[160px] text-xs"
    />
  );
}

function loadErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 502) {
    return "Couldn't load the waitlist. Try again.";
  }
  return "Couldn't load the waitlist. Check your connection and try again.";
}

function exportErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    return "Invalid date.";
  }
  if (error instanceof ApiError && error.status === 408) {
    return "The export timed out. Try again.";
  }
  if (error instanceof ApiError && error.status === 502) {
    return "Couldn't build the export. Try again.";
  }
  return "Couldn't download the waitlist. Try again.";
}

export function WaitlistPage() {
  const [page, setPage] = useState(1);
  const [minDate, setMinDate] = useState("");
  const [days, setDays] = useState(365);
  // Unset sortBy keeps today's default ordering (reached_out asc, then
  // created_at desc) on both the table and the export.
  const [sortBy, setSortBy] = useState<WaitlistSortBy | undefined>(undefined);
  const [sortDesc, setSortDesc] = useState(false);
  const queryClient = useQueryClient();

  const handleSort = (column: WaitlistSortBy) => {
    setPage(1);
    if (column === sortBy) {
      setSortDesc((d) => !d);
    } else {
      setSortBy(column);
      setSortDesc(false);
    }
  };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: queryKeys.waitlist(page, sortBy, sortDesc),
    queryFn: () => waitlistProvider.list(page, sortBy, sortDesc),
    // Emails and phone numbers — hold them no longer than the visit needs.
    gcTime: 0,
    // Changing page/sort swaps the query key; keep showing the old rows
    // instead of flashing the skeleton while the new page loads.
    placeholderData: keepPreviousData,
  });

  const updateEntry = useMutation({
    mutationFn: ({
      email,
      update,
    }: {
      email: string;
      update: Parameters<typeof waitlistProvider.update>[1];
    }) => waitlistProvider.update(email, update),
    onSuccess: (_result, { email, update }) => {
      queryClient.setQueryData<Paginated<WaitlistEntry>>(
        queryKeys.waitlist(page, sortBy, sortDesc),
        (old) =>
          old && {
            ...old,
            data: old.data.map((entry) =>
              entry.email === email ? { ...entry, ...update } : entry,
            ),
          },
      );
    },
    onError: (err) => toast.error(updateErrorMessage(err)),
  });

  const { data: historyResp, isLoading: loadingHistory } = useQuery({
    queryKey: queryKeys.waitlistHistory(days),
    queryFn: () => waitlistProvider.getHistory(days),
    staleTime: 60_000,
  });

  const download = useMutation({
    mutationFn: () =>
      waitlistProvider.exportCsv(minDate || undefined, sortBy, sortDesc),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Content-Disposition isn't readable cross-origin, so the name is built
      // here. The server dates the file in UTC; match it.
      link.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    onError: (err) => toast.error(exportErrorMessage(err)),
  });

  const entries = data?.data ?? [];
  const hasNext = data?.has_next ?? false;

  return (
    <div className="flex h-full flex-col">
      <SiteHeader title="Waitlist" />

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
        <p className="min-w-0 text-sm text-muted-foreground">
          Click a column to sort. The export uses the same sort, full list.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={minDate}
            onChange={(e) => setMinDate(e.target.value)}
            aria-label="Only include signups on or after this date"
            className="w-full sm:w-40"
          />
          <Button
            size="sm"
            className="gap-1.5"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <IconDownload className="size-4" />
            {download.isPending ? "Preparing…" : "Download CSV"}
          </Button>
        </div>
      </div>

      <div className="px-4 pb-2 lg:px-6">
        <TimeRangeSelector days={days} onDaysChange={setDays} />
      </div>

      <div className="px-4 pb-4 lg:px-6">
        <ChartWaitlistHistory
          data={historyResp?.history ?? []}
          days={days}
          onDaysChange={setDays}
          isLoading={loadingHistory}
          total={historyResp?.total}
        />
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6">
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead
                  column="email"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Email
                </SortableHead>
                <SortableHead
                  column="phone"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Phone
                </SortableHead>
                <SortableHead
                  column="created_at"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Signed up
                </SortableHead>
                <TableHead>Referral</TableHead>
                <SortableHead
                  column="app_store_email"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  App Store Email
                </SortableHead>
                <SortableHead
                  column="play_store_email"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Play Store Email
                </SortableHead>
                <SortableHead
                  column="notes"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Notes
                </SortableHead>
                <SortableHead
                  column="reached_out"
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                >
                  Reached Out
                </SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMNS }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {loadErrorMessage(error)}
                  </TableCell>
                </TableRow>
              ) : !entries.length ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {page > 1 ? "No more signups." : "No signups yet."}
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const isUpdating =
                    updateEntry.isPending &&
                    updateEntry.variables?.email === entry.email;
                  return (
                    <TableRow key={`${entry.email}-${entry.created_at}`}>
                      <TableCell className="max-w-[220px] truncate font-medium">
                        {entry.email}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <CopyId id={entry.phone} />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                        {formatDateTime(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        {entry.referral_key ? (
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-mono text-xs">
                              <IconGift className="size-3" />
                              {entry.referral_key}
                            </span>
                            {entry.referred_by && (
                              <CopyId id={entry.referred_by} />
                            )}
                          </div>
                        ) : (
                          <span className="text-xs italic text-muted-foreground opacity-40">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        <EditableEmailCell
                          value={entry.app_store_email}
                          disabled={isUpdating}
                          onSave={(next) =>
                            updateEntry.mutateAsync({
                              email: entry.email,
                              update: { app_store_email: next },
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        <EditableEmailCell
                          value={entry.play_store_email}
                          disabled={isUpdating}
                          onSave={(next) =>
                            updateEntry.mutateAsync({
                              email: entry.email,
                              update: { play_store_email: next },
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        <EditableEmailCell
                          value={entry.notes ?? null}
                          disabled={isUpdating}
                          type="text"
                          label="Edit notes"
                          clearValue=""
                          onSave={(next) =>
                            updateEntry.mutateAsync({
                              email: entry.email,
                              update: { notes: next },
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={entry.reached_out}
                          disabled={isUpdating}
                          aria-label={
                            entry.reached_out
                              ? "Mark as not reached out"
                              : "Mark as reached out"
                          }
                          onCheckedChange={(checked) =>
                            updateEntry.mutate({
                              email: entry.email,
                              update: { reached_out: checked },
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* No total count is returned, so this is Previous / Next, not pages. */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3">
          <span className="text-xs text-muted-foreground">
            Page {data?.page ?? page}
            {isFetching && " · loading…"}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <IconChevronLeft className="size-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={!hasNext || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <IconChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WaitlistPage;
